/**
 * SAT Track - the single place SAT flow events are emitted from.
 *
 * The theme already has a tracking layer in snippets/custom_events.liquid,
 * which initialises window.dataLayer and window.Moengage on every page. This
 * module deliberately does not live there: the SAT flow spans three page
 * templates with their own JS bundle, and every one of its events needs SAT
 * context (screen, assessment, product source) that the global snippet has no
 * way to see. What it does reuse is the dual-push convention - GTM and
 * MoEngage receive the same payload under the same name.
 *
 * Events are fired with the exact names the product team specified; nothing
 * here renames or namespaces them.
 */
(function (global) {
  "use strict";

  // Set by markInternalNav() immediately before a navigation that stays inside
  // the SAT flow, and read by this page's pagehide handler to decide whether
  // the user is leaving the flow or just moving through it.
  var INTERNAL_NAV_KEY = "satInternalNav";

  var config = {
    customerId: null,
  };

  function pageName() {
    // /pages/sat-ai-results -> sat-ai-results. Stable across the three SAT
    // templates and readable in a GTM report without a lookup table.
    var parts = (global.location.pathname || "").split("/").filter(Boolean);
    return parts[parts.length - 1] || "sat-ai";
  }

  /**
   * The report's assessment id, when the flow has got far enough to have one.
   * Sent as ordinary context - it is not the chat session id, which by
   * decision travels on chat_message alone.
   */
  function storedReport() {
    try {
      return (global.SATState && global.SATState.getReport()) || null;
    } catch (e) {
      return null;
    }
  }

  function assessmentId() {
    var report = storedReport();
    // A real assessment id can legitimately be 0, so this checks for absence
    // rather than falsiness.
    return report && report.assessmentId != null ? report.assessmentId : null;
  }

  function baseParams() {
    return {
      page: pageName(),
      url: global.location.href,
      customer_id: config.customerId || null,
      device_id:
        typeof global.getDeviceId === "function" ? global.getDeviceId() : null,
      assessment_id: assessmentId(),
    };
  }

  /**
   * Errors reach analytics as their raw message, not the generic customer-
   * facing line SATToast shows - the whole point of a failure event is to say
   * which failure it was.
   */
  function errorText(err) {
    if (!err) return "unknown";
    if (typeof err === "string") return err;
    return err.message || err.code || "unknown";
  }

  global.SATTrack = {
    configure: function (opts) {
      if (!opts) return;
      if ("customerId" in opts) config.customerId = opts.customerId;
    },

    /**
     * Fire one event to both sinks. Tracking must never be able to break the
     * flow, so every push is guarded - a missing or half-initialised SDK
     * costs the event, not the page.
     */
    push: function (name, params) {
      var payload = baseParams();
      Object.keys(params || {}).forEach(function (key) {
        if (params[key] !== undefined) payload[key] = params[key];
      });

      try {
        global.dataLayer = global.dataLayer || [];
        global.dataLayer.push(
          Object.assign({ event: name }, payload)
        );
      } catch (e) {
        console.warn("[SATTrack] dataLayer push failed:", name, e);
      }

      try {
        if (global.Moengage && typeof global.Moengage.track_event === "function") {
          global.Moengage.track_event(name, payload);
        }
      } catch (e) {
        console.warn("[SATTrack] MoEngage push failed:", name, e);
      }
    },

    /** A failure event, with the underlying message attached. */
    error: function (name, err, params) {
      this.push(
        name,
        Object.assign({ error_message: errorText(err) }, params || {})
      );
    },

    /**
     * Fire at most once per assessment. assessment_started has several entry
     * points and the landing screen is re-rendered on browser back, so without
     * this the top of the funnel would be wider than the traffic.
     *
     * The flag lives in sessionStorage next to the rest of the SAT state and
     * is cleared by resetSession(), which the "start a new test" paths call.
     */
    pushOnce: function (name, params) {
      var key = "satTracked:" + name;
      try {
        if (global.sessionStorage.getItem(key)) return;
        global.sessionStorage.setItem(key, "1");
      } catch (e) {
        // Private mode and the like: better a duplicate event than none.
      }
      this.push(name, params);
    },

    /** A fresh assessment gets a fresh set of once-only events. */
    resetSession: function () {
      try {
        Object.keys(global.sessionStorage).forEach(function (key) {
          if (key.indexOf("satTracked:") === 0) {
            global.sessionStorage.removeItem(key);
          }
        });
      } catch (e) {}
    },

    /**
     * Call immediately before any navigation that stays inside the SAT flow
     * (shell -> analysis -> results, or back to the landing page for a
     * retake). watchExit() reads this to tell "moved on within the flow" from
     * "left the flow", which is what assessment_closed is meant to count.
     */
    markInternalNav: function () {
      try {
        global.sessionStorage.setItem(INTERNAL_NAV_KEY, "1");
      } catch (e) {}
    },

    /**
     * Fire assessment_closed when the user leaves the SAT pages.
     *
     * pagehide rather than beforeunload: it is the one unload signal that also
     * fires on mobile Safari and on bfcache navigations, which is where most
     * of this traffic is. `getContext` supplies the screen the user was on -
     * only the section knows that.
     */
    watchExit: function (getContext) {
      var self = this;

      // The flag is set and read within one page's lifetime, so anything left
      // over belongs to a previous page - a click that marked a navigation
      // which then did not happen. Clearing it here keeps that from
      // swallowing this page's exit.
      try {
        global.sessionStorage.removeItem(INTERNAL_NAV_KEY);
      } catch (e) {}

      global.addEventListener("pagehide", function () {
        var internal = false;
        try {
          internal = global.sessionStorage.getItem(INTERNAL_NAV_KEY) === "1";
          global.sessionStorage.removeItem(INTERNAL_NAV_KEY);
        } catch (e) {}

        if (internal) return;

        var extra = {};
        try {
          extra = (typeof getContext === "function" && getContext()) || {};
        } catch (e) {}

        self.push(
          "assessment_closed",
          // "Completed" means the report was generated, not that anything was
          // bought - a stored report is the only durable marker of that.
          Object.assign({ completed: !!storedReport() }, extra)
        );
      });
    },
  };
})(window);
